/* Leo Dashboard quick catalog upload. Shares existing Supabase storage and cover pipeline. */
(() => {
  'use strict';
  const proto = window.AAHeadquartersDashboard?.prototype;
  if (!proto) return;
  proto.renderLeoUploadForm = function() {
    const escape = value => this.escapeHtml(value ?? '');
    const clients = (this.clients || [])
      .filter(c => String(c.active_status || '').toLowerCase() !== 'archived')
      .slice().sort((a,b) => String(a.business_name || '').localeCompare(String(b.business_name || '')));
    const options = clients.map(c => '<option value="' + escape(c.id) + '">' +
      escape(c.business_name || 'Unnamed Client') + '</option>').join('');
    const html = [
      '<form class="aa-dashboard-leo-upload" data-aa-dashboard-leo-upload-form>',
      '<div class="aa-dashboard-leo-upload__heading"><div><strong>Add a catalog</strong>',
      '<p>Choose the client, month, and PDF. Current and future catalogs appear in Leo; earlier months save to Client Catalog History.</p>',
      '</div><button type="button" class="aa-dashboard-leo-upload__close"',
      ' data-aa-dashboard-leo-upload-cancel aria-label="Close catalog upload">×</button></div>',
      '<div class="aa-dashboard-leo-upload__fields">',
      '<label>Client<select name="clientId" required>',
      '<option value="">Select client</option>', options, '</select></label>',
      '<label>Catalog month<input type="month" name="catalogMonth" value="',
      escape(this.getCurrentMonthValue()), '" required></label>',
      '<label>Catalog PDF<input type="file" name="catalogPdf" accept="application/pdf,.pdf" required></label>',
      '</div><details class="aa-dashboard-leo-upload__optional">',
      '<summary>Optional catalog details</summary>',
      '<div class="aa-dashboard-leo-upload__fields">',
      '<label>Catalog title<input type="text" name="catalogTitle" maxlength="180"',
      ' placeholder="Defaults to catalog month"></label>',
      '<label>Brands represented<input type="text" name="catalogBrands"',
      ' placeholder="Separate brands with commas"></label>',
      '</div></details>',
      '<div class="aa-dashboard-leo-upload__footer">',
      '<button type="submit" class="aa-dashboard-leo-action aa-dashboard-leo-action--primary">Upload Catalog</button>',
      '<span data-aa-dashboard-leo-upload-feedback role="status"></span>',
      '</div></form>'
    ].join('');
    return window.AACatalogMonth
      ? window.AACatalogMonth.enhance(html, 'input[name="catalogMonth"]') : html;
  };
  proto.saveLeoCatalog = async function(form) {
    if (this.leoUploadBusy) return;
    const clientId = form.elements.namedItem('clientId')?.value || '';
    const month = window.AACatalogMonth
      ? window.AACatalogMonth.read(form, 'input[name="catalogMonth"]')
      : (form.elements.namedItem('catalogMonth')?.value || '');
    const file = form.elements.namedItem('catalogPdf')?.files?.[0] || null;
    const feedback = form.querySelector('[data-aa-dashboard-leo-upload-feedback]');
    const report = msg => { if (feedback) feedback.textContent = msg; };
    const client = (this.clients || []).find(c => String(c.id) === String(clientId));
    if (!client || !/^\d{4}-(0[1-9]|1[0-2])$/.test(month)) {
      report('Choose a client, Month, and Year.');
      return;
    }
    if (!file || file.type !== 'application/pdf' || file.size > 75 * 1024 * 1024) {
      report('Choose a PDF no larger than 75 MB.');
      return;
    }
    const api = window.AASupabaseClient;
    const db = api?.getClient?.();
    if (!db) { report('Catalog connection is unavailable.'); return; }
    const button = form.querySelector('button[type="submit"]');
    this.leoUploadBusy = true;
    if (button) {button.disabled = true; button.textContent = 'Uploading...';}
    let uploadedPdf = null;
    let uploadedCover = null;
    let catalogSaved = false;
    try {
      // Duplicate identity is (client_id, catalog_month); Oct never blocks Nov.
      const {data: existing, error: checkError} = await db.from('aa_catalog_records')
        .select('id,catalog_month').eq('client_id',clientId)
        .eq('catalog_month',month + '-01').eq('status','active').limit(1);
      if (checkError) throw checkError;
      if (existing?.length) {
        this.leoUploadOpen = false;
        this.leoExpanded = true;
        this.leoUploadMessage = this.formatMonth(month) + ' already exists for ' +
          client.business_name + '. No duplicate was uploaded.';
        await this.refreshLeoCatalogs();
        return;
      }
      report('Uploading the PDF and creating its cover...');
      uploadedPdf = await api.uploadCatalogPdf({clientId,file});
      // Reuse the same first-page cover renderer as Clients, not a second implementation.
      const coverCreator = window.AAHeadquartersClients?.prototype?.createCatalogCoverFromPdf;
      if (typeof coverCreator !== 'function')
        throw Error('Catalog cover service is unavailable. Please refresh.');
      uploadedCover = await coverCreator.call(null,{clientId,file});
      if (!uploadedCover?.publicUrl) throw Error('Catalog cover could not be created.');
      const customTitle = form.elements.namedItem('catalogTitle')?.value.trim() || '';
      const brands = [...new Set(String(form.elements.namedItem('catalogBrands')?.value || '')
        .split(',').map(name=>name.trim()).filter(Boolean))];
      const created = await api.createCatalogRecord({
        clientId, catalogMonth:month, publicationDate:month+'-01',
        catalogTitle:customTitle || this.formatMonth(month)+' Catalog',
        catalogUrl:'', coverImageUrl:uploadedCover.publicUrl,
        catalogViews:null, brands,
        catalogPdfStoragePath:uploadedPdf.path,
        catalogPdfFileName:uploadedPdf.fileName,
        catalogPdfFileSize:uploadedPdf.fileSize
      });
      if (!created?.id) throw Error('Catalog record could not be saved.');
      catalogSaved = true;
      this.leoUploadOpen = false;
      this.leoExpanded = true;
      this.leoUploadMessage = month < this.getCurrentMonthValue()
        ? this.formatMonth(month) + ' catalog saved for ' + client.business_name + ' in Client Catalog History.'
        : this.formatMonth(month) + ' catalog saved for ' + client.business_name + '. You can now generate tasks.';
      await this.refreshLeoCatalogs();
    } catch(error) {
      if(!catalogSaved && uploadedPdf?.path) {
        await api.deleteCatalogPdf({path:uploadedPdf.path}).catch(()=>{});
      }
      if(!catalogSaved && uploadedCover?.path) {
        await db.storage.from('catalog-covers').remove([uploadedCover.path]).catch(()=>{});
      }
      console.warn('Leo quick catalog upload failed:', error);
      report(error?.code === 'AA_DUPLICATE_CATALOG_MONTH'
        ? 'A catalog already exists for this client and month. Choose another month.'
        : error?.message || 'Unable to save catalog. Try again.');
    } finally {
      this.leoUploadBusy = false;
      if(button?.isConnected){button.disabled = false;button.textContent='Upload Catalog';}
    }
  };
})();
